/**
 * recruitment-media-interface.js
 * 媒体連携関連APIのI/F定義
 */

// ファイル処理リクエスト用I/F
import fileProcessingInterface from '@/api/general-purpuse/file-processing-interface'

// プロトタイプ宣言追加メソッド
import '@/hrm-frontend/modules/functions/prototype-extends'

/**
 * 媒体連携関連APIのI/F定義クラス
 */
export default class RecruitmentMediaApiInterface {
  /**
   * コンストラクター
   */
  constructor () {
    // ペイロード要素
    this.PAYLOAD_ITEM = {
      // 処理種別
      EXECUTE_TYPE: 'execute-type',
      // データ
      DATA: 'data'
    }
    // 処理種別
    this.EXECUTE_TYPE = {
      // 媒体情報新規作成
      CREATE_RECRUITMENT_MEDIA: 'create-recruitment-media',
      // 全媒体情報取得
      GET_ALL_RECRUITMENT_MEDIA: 'get-all-recruitment-media',
      // 媒体情報更新
      UPDATE_RECRUITMENT_MEDIA: 'update-recruitment-media',
      // 媒体情報削除
      DELETE_RECRUITMENT_MEDIA: 'delete-recruitment-media',
      // 選考状況リスト取得
      GET_PROGRESS_LIST: 'get-progress-list',
      // Seekerフィールドリスト取得
      GET_SEEKER_FIELD_LIST: 'get-seeker-field-list',

      // 媒体連携設定新規作成
      CREATE_MEDIA_COOPERATION_SETTING: 'create-media-cooperation-setting',
      // 媒体連携設定取得
      GET_MEDIA_COOPERATION_SETTING: 'get-media-cooperation-setting',
      // 媒体連携設定更新
      UPDATE_MEDIA_COOPERATION_SETTING: 'update-media-cooperation-setting',
      // 媒体連携設定削除
      DELETE_MEDIA_COOPERATION_SETTING: 'delete-media-cooperation-setting',

      // 新規作成
      CREATE_ITEM_MASTER: 'create-item-master',
      // 新規作成(非同期)
      CREATE_ITEM_MASTER_ASYNC: 'create-item-master-async',
      // 新規作成(非同期)
      GET_CREATE_ITEM_MASTER_ASYNC_TASK: 'get-create-item-master-async-task',
      // 媒体連携項目マスター一覧取得
      GET_ITEM_MASTER_LIST: 'get-item-master-list',
      // 媒体連携項目マスターテンプレート取得
      GET_ITEM_MASTER_TEMPLATE: 'get-item-master-template',
      // 媒体連携項目マスターデータ取得
      GET_ITEM_MASTER_DATA: 'get-item-master-data',
      // 媒体連携項目マスター名更新
      UPDATE_ITEM_MASTER_NAME: 'update-item-master-name',
      // 媒体連携項目マスターデータ更新
      UPDATE_ITEM_MASTER_DATA: 'update-item-master-data',
      // 媒体連携項目マスターモデル更新
      UPDATE_ITEM_MASTER_MODEL: 'update-item-master-model',
      // 媒体連携項目マスター削除
      DELETE_ITEM_MASTER: 'delete-item-master',
      // 媒体連携項目マスターインポート
      IMPORT_ITEM_MASTER: 'import-item-master',
      // 媒体連携項目マスターエクスポート
      EXPORT_ITEM_MASTER: 'export-item-master',
      // 媒体連携項目マスター行追加
      ADD_ITEM_ROW: 'add-item-row',
      // 媒体連携項目マスター行削除
      DEL_ITEM_ROW: 'del-item-row',
      // 媒体連携項目マスター編集キャンセル
      EDIT_CANCEL: 'edit-cancel',
      // 媒体連携項目マスター編集後処理
      EDIT_TERMINATE: 'edit-terminate'
    }
    // 媒体連携項目マスター必須フィールド
    this.MEDIA_COOPERATION_ITEM_MASTER_FIELD = {
      PARENT: [
        'company',
        'master_id',
        'master_name',
        'is_working',
        'order',
        'data_row_count',
        'common_item_data',
        'media_item_data'
      ],
      COMMON_HEADER_ITEM_LIST: [
        'media_list_id',
        'media_name',
        'is_apply',
        'applicant_column',
        'item_list',
      ],
      MEDIA_HEADER_ITEM_LIST: [
        'media_list_id',
        'company_recruit',
        'media_id',
        'media_name',
        'media_name_short',
        'is_apply',
        'media_column',
        'item_list',
      ],
      ITEM_DATA_LIST: [
        'id',
        'row_no',
        'item_data'
      ]
    }
    // ペイロード初期値
    this.payload = {
      // 処理種別
      [this.PAYLOAD_ITEM.EXECUTE_TYPE]: '',
      // データ
      [this.PAYLOAD_ITEM.DATA]: {}
    }
  }

  // ==================================================
  // 媒体情報関連
  // ==================================================

  /**
   * 全媒体情報取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeAllRecruitmentMediaInfoParameter () {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 全媒体情報取得
        this.EXECUTE_TYPE.GET_ALL_RECRUITMENT_MEDIA
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 選考状況リスト取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeGetProgressListParameter () {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 選考状況リスト取得
        this.EXECUTE_TYPE.GET_PROGRESS_LIST
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * Seekerフィールドリスト取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeGetSeekerFieldListParameter () {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // Seekerフィールドリスト取得
        this.EXECUTE_TYPE.GET_SEEKER_FIELD_LIST
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  // ==================================================
  // 媒体連携項目マスター関連
  // ==================================================

  /**
   * 媒体連携項目マスターデータチェック
   * @param {Object} itemMaster 媒体連携項目マスターデータ
   * @returns チェック結果
   */
  checkItemMasterData (itemMaster) {
    try {
      let result = {
        isSuccess: true,
        isUserError: false,
        isProductError: false,
        userErrorMessage: [],
        productErrorMessage: [],
      }

      // マスター名チェック
      if (itemMaster.master_name === '') {
        result.isSuccess = false
        result.isUserError = true
        result.userErrorMessage.push('マスター名が入力されていません。')
      }
      // 共通項目選択チェック
      if (!itemMaster.common_item_data.header.applicant_column) {
        // 基本的にはデフォルトで先頭の項目が選択されている
        result.isSuccess = false
        result.isUserError = true
        result.userErrorMessage.push('共通項目が選択されていません。')
      }
      // 媒体側項目選択チェック
      itemMaster.media_item_data.forEach(item_data => {
        // 基本的にはデフォルトで先頭の項目が選択されている
        if (!item_data.header.media_column && item_data.header.is_apply === true) {
          result.isSuccess = false
          result.isUserError = true
          result.userErrorMessage.push(
            '{0}({1})の項目が選択されていません。'.format(
              item_data.header.media_name_short,
              item_data.header.company_recruit_account
            )
          )
        }
      })

      // 分割保存した際にマスター全体での行数を考慮して算出できないためコメントアウト
      // // 空行削除
      // let commonMaxRowNo = 0
      // // 未使用媒体にデータが設定されていて、他が空の場合もあり得る
      // // 共通項目と全媒体のデータが空の場合にだけ削除する
      // itemMaster.common_item_data.data.forEach((item_data, index) => {
      //   if (item_data.item_data) {
      //     // 空行でなければ最大行数を更新する
      //     commonMaxRowNo = index
      //   }
      // })

      // let mediaMaxRowNo = 0
      // itemMaster.media_item_data.forEach(media_data => {
      //   media_data.data.forEach((item_data, index) => {
      //     if (item_data.item_data) {
      //       // 空行のインデックスを保持する
      //       // 1行目が空行の場合はそのまま保存する
      //       if (mediaMaxRowNo < index) {
      //         mediaMaxRowNo = index
      //       }
      //     }
      //   })
      // })
      // // 削除開始行
      // const dataMaxRowNo = commonMaxRowNo > mediaMaxRowNo ? commonMaxRowNo : mediaMaxRowNo
      // itemMaster.data_row_count = dataMaxRowNo + 1
      // const deleteStartRowNo = dataMaxRowNo + 1
      // // 最後に見付けた全ての共通項目および媒体項目で空の行以降を削除する
      // // 1行目は空でも削除しない
      // if (deleteStartRowNo > 0) {
      //   if (deleteStartRowNo < itemMaster.common_item_data.data.length) {
      //     itemMaster.common_item_data.data.splice(deleteStartRowNo, itemMaster.common_item_data.data.length - deleteStartRowNo)
      //   }
      //   itemMaster.media_item_data.forEach(media_data => {
      //     if (deleteStartRowNo < media_data.data.length) {
      //       media_data.data.splice(deleteStartRowNo, media_data.data.length - deleteStartRowNo)
      //     }
      //   })
      // }

      // 更新データ形式チェック
      // 媒体連携項目マスター
      const item_master_key = Object.keys(itemMaster)
      this.MEDIA_COOPERATION_ITEM_MASTER_FIELD.PARENT.forEach(key => {
        if (item_master_key.includes(key) === false) {
          result.isSuccess = false
          result.isProductError = true
          result.productErrorMessage.push(
            '項目マスターに必要な項目が含まれていません。[{0}]'.format(key)
          )
          return
        }
      })
      // 共通項目データ
      const common_list_key = Object.keys(itemMaster.common_item_data.header)
      this.MEDIA_COOPERATION_ITEM_MASTER_FIELD.COMMON_HEADER_ITEM_LIST.forEach(key => {
        if (common_list_key.includes(key) === false) {
          result.isSuccess = false
          result.isProductError = true
          result.productErrorMessage.push(
            '共通項目のヘッダーデータに必要な項目が含まれていませんでした。[{0}]'.format(key)
          )
          return
        }
      })
      itemMaster.common_item_data.data.forEach((item_data, index) => {
        const common_item_key = Object.keys(item_data)
        this.MEDIA_COOPERATION_ITEM_MASTER_FIELD.ITEM_DATA_LIST.forEach(key => {
          if (common_item_key.includes(key) === false) {
            result.isSuccess = false
            result.isProductError = true
            result.productErrorMessage.push(
              '共通項目のデータリストに必要な項目が含まれていませんでした。[{0}行目; {1}]'.format(index, key)
            )
            return
          }
        })
      })
      // 媒体項目データ
      itemMaster.media_item_data.forEach(media_data => {
        const media_list_key = Object.keys(media_data.header)
        this.MEDIA_COOPERATION_ITEM_MASTER_FIELD.MEDIA_HEADER_ITEM_LIST.forEach(key => {
          if (media_list_key.includes(key) === false) {
            result.isSuccess = false
            result.isProductError = true
            result.productErrorMessage.push(
              '媒体項目のヘッダーデータに必要な項目が含まれていませんでした。[{0}({1}): {2}]'.format(
                media_data.header.media_name,
                media_data.header.company_recruit_account,
                key
              )
            )
            return
          }
        })
        media_data.data.forEach((item_data, index) => {
          const media_item_key = Object.keys(item_data)
          this.MEDIA_COOPERATION_ITEM_MASTER_FIELD.ITEM_DATA_LIST.forEach(key => {
            if (media_item_key.includes(key) === false) {
              result.isSuccess = false
              result.isProductError = true
              result.productErrorMessage.push(
                '媒体項目のヘッダーデータに必要な項目が含まれていませんでした。[{0}({1})の{2}行目: {3}]'.format(
                  media_data.header.media_name,
                  media_data.header.company_recruit_account,
                  index,
                  key
                )
              )
              return
            }
          })
        })
      })
      return result
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスター新規作成
   * @param {Object} itemMaster 媒体連携項目マスター更新データ
   * @returns ペイロード
   */
  makeItemMasterCreatePayload (itemMaster) {
    try {
      // 媒体連携項目マスターデータチェック
      const checkResult = this.checkItemMasterData(itemMaster)
      if (checkResult.isSuccess === false) {
        return checkResult
      }
      // 処理種別：新規作成
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.CREATE_ITEM_MASTER_ASYNC
      // リクエストデータ：媒体連携項目マスターデータ
      this.payload[this.PAYLOAD_ITEM.DATA] = itemMaster

      // ペイロードを返す
      return {isSuccess: true, payload: this.payload}
    } catch (error) {
      throw error
    }
  }

  /**
   * タスクステータス関連GETリクエストペイロード作成
   * @param {Number} taskId タスクID
   * @returns ペイロード
   */
  createGetCreateItemMasterAsyncTaskParameter(taskId) {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}&task-id={2}'.format(
        // 処理種別： 非同期タスクステータス取得
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // リクエストデータ： 取得するタスクID
        this.EXECUTE_TYPE.GET_CREATE_ITEM_MASTER_ASYNC_TASK,
        // タスクId
        taskId
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスターデータ取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeGetItemMasterDataParameter (data) {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}&master-id={2}&current-page={3}&row-range={4}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 媒体連携項目マスターデータ取得
        this.EXECUTE_TYPE.GET_ITEM_MASTER_DATA,
        // 媒体連携項目マスターID
        data.master_id,
        // 取得ページ番号
        data.current_page,
        // 取得件数
        data.row_range
      )
      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスター一覧取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeGetItemMasterListParameter () {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 媒体連携項目マスター一覧取得
        this.EXECUTE_TYPE.GET_ITEM_MASTER_LIST
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスターエクスポートクエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeItemMasterDataExportParameter (masterId) {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}&master-id={2}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 媒体連携項目マスターエクスポート
        this.EXECUTE_TYPE.EXPORT_ITEM_MASTER,
        masterId
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 新規媒体連携項目マスターテンプレート取得クエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeItemMasterGetTemplateParameter () {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 媒体連携項目マスターテンプレート取得
        this.EXECUTE_TYPE.GET_ITEM_MASTER_TEMPLATE
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスター名変更ペイロード作成
   * @param {Object} itemMaster 媒体連携項目マスター
   * @returns ペイロード
   */
  makeItemMasterNameSavePayload (itemMasterList) {
    try {
      let result = {
      isSuccess: false,
      payload: null
      }
      if (itemMasterList.length > 0) {
        // マスター名更新用データ
        const nameUpdatePayload = {
          update_data: itemMasterList
        }
        // 処理種別：更新
        this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.UPDATE_ITEM_MASTER_MODEL
        // リクエストデータ：媒体連携項目マスターデータ
        this.payload[this.PAYLOAD_ITEM.DATA] = nameUpdatePayload

        // データをセット
        result.isSuccess = true
        result.payload = this.payload
      }
      // ペイロードを返す
      return result
    } catch (error) {
      throw error
    }
  }
  /**
   * 媒体連携項目マスター保存ペイロード作成
   * @param {Object} itemMaster 媒体連携項目マスター更新データ
   * @returns ペイロード
   */
  makeItemMasterSavePayload (itemMaster) {
    try {
      // 媒体連携項目マスターデータチェック
      const checkResult = this.checkItemMasterData(itemMaster)
      if (checkResult.isSuccess === false) {
        return checkResult
      }

      // 処理種別：更新
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.UPDATE_ITEM_MASTER_DATA
      // リクエストデータ：媒体連携項目マスターデータ
      this.payload[this.PAYLOAD_ITEM.DATA] = itemMaster

      // ペイロードを返す
      return {isSuccess: true, payload: this.payload}
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスター削除クエリパラメータ作成
   * @param {Number} masterId 媒体連携項目マスターID
   * @returns クエリパラメーター
   */
  makeItemMasterDataDeleteParameter (masterId) {
    try {
      // クエリパラメーターを作成
      const queryParameter = '{0}={1}&master-id={2}'.format(
        // 処理種別
        this.PAYLOAD_ITEM.EXECUTE_TYPE,
        // 媒体連携項目マスター削除
        this.EXECUTE_TYPE.DELETE_ITEM_MASTER,
        // 媒体連携項目マスターID
        masterId
      )

      // クエリパラメーターを返す
      return queryParameter
    } catch (error) {
      throw error
    }
  }

  /**
   * CSVテンプレートダウンロードペイロード作成
   * @returns ペイロード
   */
  makeItemMasterCsvTemplateDownloadPayload (currentMaster, masterName) {
    try {
      if (masterName) {
        // ファイル処理リクエスト用のインターフェースを使ってペイロードを作成する
        const fileProcessingIf = new fileProcessingInterface()
        return fileProcessingIf.makeItemMasterCsvTemplateDownloadPayload(currentMaster, masterName)
      } else {
        alert('マスター名が入力されていません。')
        return null
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスターデータCSVエクスポートクエリパラメータ作成
   * @returns クエリパラメーター
   */
  makeItemMasterDataExportParameter (masterId) {
    try {
      // ファイル処理リクエスト用のインターフェースを使ってクエリパラメーターを作成する
      const fileProcessingIf = new fileProcessingInterface()
      return fileProcessingIf.makeItemMasterDataExportParameter(masterId)
    } catch (error) {
      throw error
    }
  }

  /**
   * 媒体連携項目マスターインポートペイロード作成
   * @param {file} importFile 媒体連携項目マスターインポートファイル
   * @returns ペイロード
   */
  makeItemMasterDataImportPayload (importFile) {
    try {
      // ファイル処理リクエスト用のインターフェースを使ってペイロードを作成する
      const fileProcessingIf = new fileProcessingInterface()
      return fileProcessingIf.makeItemMasterDataImportPayload(importFile)
    } catch (error) {
      throw error
    }
  }

  /**
   * 編集キャンセルペイロード作成
   * @param {Number} masterId 媒体連携項目マスターID
   * @returns ペイロード
   */
  editCancelPayload (masterId) {
    try {
      // 処理種別：媒体連携項目マスター編集キャンセル
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.EDIT_CANCEL
      // リクエストデータ：媒体連携項目マスターID
      this.payload[this.PAYLOAD_ITEM.DATA] = {
        master_id: masterId
      }

      // ペイロードを返す
      return [true, this.payload]
    } catch (error) {
      throw error
    }
  }

  /**
   * 編集後処理ペイロード作成
   * @param {Number} masterId 媒体連携項目マスターID
   * @returns ペイロード
   */
  editTerminatePayload (masterId) {
    try {
      // 処理種別：媒体連携項目マスター編集後処理
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.EDIT_TERMINATE
      // リクエストデータ：媒体連携項目マスターID
      this.payload[this.PAYLOAD_ITEM.DATA] = {
        master_id: masterId
      }

      // ペイロードを返す
      return [true, this.payload]
    } catch (error) {
      throw error
    }
  }

  /**
   * 項目行追加ペイロード作成
   * @param {Number} masterId 媒体連携項目マスターID
   * @param {Number} rowNo 追加行番号
   * @returns ペイロード
   */
  addItemRowPayload(masterId, rowNo) {
    try {
      // 処理種別：媒体連携項目マスター行追加
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.ADD_ITEM_ROW
      // リクエストデータ：追加行番号
      this.payload[this.PAYLOAD_ITEM.DATA] = {
        master_id: masterId,
        row_no: rowNo
      }

      // ペイロードを返す
      return [true, this.payload]
    } catch (error) {
      throw error
    }
  }

  /**
   * 項目行削除ペーロード作成
   * @param {Number} masterId 媒体連携項目マスターID
   * @param {Number} rowNo 削除行番号
   * @param {Array} delRowIdList 削除項目データIDリスト
   * @returns ペイロード
   */
  delItemRowPayload (masterId, rowNo, delRowIdList) {
    try {
      // 処理種別：媒体連携項目マスター行削除
      this.payload[this.PAYLOAD_ITEM.EXECUTE_TYPE] = this.EXECUTE_TYPE.DEL_ITEM_ROW
      // リクエストデータ：削除行番号
      this.payload[this.PAYLOAD_ITEM.DATA] = {
        master_id: masterId,
        row_no: rowNo,
        del_row_id_list: delRowIdList
      }

      // ペイロードを返す
      return [true, this.payload]
    } catch (error) {
      throw error
    }
  }
}



// WEBPACK FOOTER //
// ./src/api/recruitment-media/recruitment-media-interface.js